import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Radio, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, SectionHead } from "@/components/Shared";
import { SunMark } from "@/components/Sun";
import { BRAND, SERVICES } from "@/data/site";
import { url, metaContact, breadcrumbLd, orgLd } from "@/data/content";

export default function Contact() {
  const meta = metaContact();
  const crumbs = [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact/" }];
  return (
    <div data-testid="contact-page">
      <Seo title={meta.title} description={meta.description} path="/contact/" jsonLd={[orgLd(), breadcrumbLd(crumbs)]} />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 relative">
          <Breadcrumbs items={crumbs} />
          <div className="flex items-center gap-4 mb-6"><SunMark size={52} /></div>
          <h1 data-testid="contact-h1" className="text-4xl sm:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-3xl">
            Contact Our 24/7 Dispatch Desk
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">
            No forms, no queues, no waiting on callbacks. Call or email and speak directly with an operations professional — a written, itemized quote the same day, anywhere in Canada.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
        <div className="grid sm:grid-cols-2 gap-5 mb-14">
          <a href={BRAND.phoneHref} data-testid="contact-phone-link" className="group rounded-2xl border border-amber-500/30 bg-[#111827] p-8 transition-all hover:border-amber-500/60 hover:-translate-y-1">
            <div className="h-12 w-12 rounded-xl bg-amber-500 flex items-center justify-center mb-5">
              <Phone className="h-6 w-6 text-slate-950" />
            </div>
            <h2 className="font-display font-bold text-lg text-slate-50 mb-1">Call Dispatch — 24/7</h2>
            <p className="font-mono text-2xl font-bold text-amber-400 mb-2">{BRAND.phone}</p>
            <p className="text-sm text-slate-400">Emergency guard deployment available tonight in most cities.</p>
          </a>
          <a href={BRAND.emailHref} data-testid="contact-email-link" className="group rounded-2xl border border-slate-800 bg-[#111827] p-8 transition-all hover:border-amber-500/60 hover:-translate-y-1">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mb-5">
              <Mail className="h-6 w-6 text-amber-400" />
            </div>
            <h2 className="font-display font-bold text-lg text-slate-50 mb-1">Email Us</h2>
            <p className="font-mono text-base sm:text-lg font-bold text-amber-400 mb-2 break-all">{BRAND.email}</p>
            <p className="text-sm text-slate-400">Quotes, contracts, tenders and RFPs welcome.</p>
          </a>
          <div className="rounded-2xl border border-slate-800 bg-[#111827] p-8">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mb-5">
              <Clock className="h-6 w-6 text-amber-400" />
            </div>
            <h2 className="font-display font-bold text-lg text-slate-50 mb-1">Hours of Operation</h2>
            <p className="font-mono text-lg font-bold text-slate-100 mb-2">24/7/365</p>
            <p className="text-sm text-slate-400">Our dispatch desk never closes — nights, weekends and holidays included.</p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-[#111827] p-8">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mb-5">
              <MapPin className="h-6 w-6 text-amber-400" />
            </div>
            <h2 className="font-display font-bold text-lg text-slate-50 mb-1">Home Base</h2>
            <p className="font-mono text-lg font-bold text-slate-100 mb-2">{BRAND.homeBase}</p>
            <p className="text-sm text-slate-400">Serving every province and territory — 107 cities and counting.</p>
          </div>
        </div>

        <section className="rounded-2xl border border-slate-800 bg-[#111827] p-8 mb-6">
          <div className="flex items-center gap-2 text-amber-500 font-mono text-xs uppercase tracking-[0.25em] mb-4">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> What to have ready when you call
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-slate-300">
            <p className="rounded-xl bg-slate-900/60 border border-slate-800 px-4 py-3">1. Your site address and city</p>
            <p className="rounded-xl bg-slate-900/60 border border-slate-800 px-4 py-3">2. Hours and days you need covered</p>
            <p className="rounded-xl bg-slate-900/60 border border-slate-800 px-4 py-3">3. The service type or risk concern</p>
            <p className="rounded-xl bg-slate-900/60 border border-slate-800 px-4 py-3">4. Any insurance or site requirements</p>
          </div>
        </section>

        <SectionHead eyebrow="Explore First" title="Not ready to call? Browse the services" desc="Every service page explains scope, coverage and pricing approach in plain language." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={url.service(s)} className="group flex items-center justify-between rounded-lg border border-slate-800 bg-[#111827] px-4 py-3 text-sm text-slate-300 transition-all hover:border-amber-500/50 hover:text-amber-400">
              {s.name}
              <ArrowRight className="h-3.5 w-3.5 text-amber-500/60 transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
